import {
  v7 as uuidv7,
  validate as uuidValidate,
  version as uuidVersion,
} from "uuid";
import { createLogger } from "./debug";

const logger = createLogger("UUID7");

// Fixed fallback timestamp for invalid or non-UUID7 values to ensure deterministic behavior.
// Using a fixed past date that is clearly distinguishable from valid timestamps
// and easy to spot in logs/DB when debugging.
const FALLBACK_TIMESTAMP = "2020-01-01T00:00:00.000Z";

/**
 * Validates that a string is a proper UUID7 format using the uuid library
 * @param uuid - The UUID string to validate
 * @returns true if valid UUID7, false otherwise
 */
export const isValidUUID7 = (uuid: string): boolean => {
  return uuidValidate(uuid) && uuidVersion(uuid) === 7;
};

/**
 * Extracts the timestamp encoded in a UUID7 string.
 * UUID7 embeds a Unix timestamp with millisecond precision in the first 48 bits.
 *
 * Both client- and server-generated UUID7s are valid inputs. This helper only
 * parses and returns the embedded timestamp; the returned value reflects
 * whatever clock minted the UUID7 and is only as trustworthy as that clock.
 *
 * @param uuid7 - The UUID7 string
 * @returns ISO string timestamp
 */
export const extractTimestampFromUUID7 = (uuid7: string): string => {
  try {
    if (!isValidUUID7(uuid7)) {
      return FALLBACK_TIMESTAMP;
    }

    const hex = uuid7.replaceAll("-", "");
    const timestampHex = hex.substring(0, 12);
    const timestampMs = Number.parseInt(timestampHex, 16);

    return new Date(timestampMs).toISOString();
  } catch (error) {
    logger.error("Error extracting timestamp from UUID7", error);
    return FALLBACK_TIMESTAMP;
  }
};

/**
 * Generates a new UUID7 string.
 * Central helper so callers don’t depend directly on the uuid library.
 */
export const generateUuid7 = (): string => uuidv7();
